import React from "react";
import { useAppSelector, useThunkDispatch } from "../../../store";
import { selectCycle } from "../../../store/profiling/actions";
import { groupSummaries, cycleTree, type GroupSummary, type CycleTreeNode } from "../../../store/profiling/selectors";
import type { CycleEntry } from "../../../store/profiling/types";
import { ProfilingTooltip, TipRow, TipTitle, TipDivider } from "./ProfilingTooltip";
import { shortCycleId, formatMs, summarizeCycle, TRIGGER_LABELS } from "./viz";

// A small custom tree (rows + chevrons). MUI's SimpleTreeView is a viable alternative now that the
// pop-out has its own Emotion cache, but this stays lightweight for the group/cycle rows we render.

const INDENT = 14;

// What a hovered row represents (drives the tooltip content).
type HoverTarget =
    | { kind: "group"; group: GroupSummary }
    | { kind: "cycle"; cycle: CycleEntry };

// The hovered target plus the pointer position (for the tooltip anchor).
type Hover = (HoverTarget & { x: number; y: number }) | null;

// --- Tooltip content ---------------------------------------------------------

function GroupTooltipContent({ group }: { group: GroupSummary }) {
    return (
        <>
            <TipTitle>Render pass</TipTitle>
            <TipRow label="Root cycle" value={group.rootCycleId} />
            <TipRow label="Template" value={group.fileName ?? "(inline)"} />
            <TipDivider />
            <TipRow label="Wall time" value={formatMs(group.wallTime)} />
            <TipRow label="Templates" value={group.templateCount} />
            <TipRow label="Cycles" value={group.cycleCount} />
            <TipRow label={TRIGGER_LABELS.callback} value={group.callbackCount || "none"} />
            <TipRow label={TRIGGER_LABELS.react} value={group.reactCount || "off"} />
        </>
    );
}

function CycleTooltipContent({ cycle }: { cycle: CycleEntry }) {
    const s = summarizeCycle(cycle);
    return (
        <>
            <TipTitle>Template render</TipTitle>
            <TipRow label="Cycle" value={cycle.cycleId} />
            <TipRow label="Template" value={cycle.fileName ?? "(inline)"} />
            <TipDivider />
            <TipRow label="Wall time" value={formatMs(s.wallTime)} />
            <TipRow label="Render" value={`${formatMs(s.renderTotal)} · ${s.renderNodes} node${s.renderNodes === 1 ? "" : "s"}`} />
            <TipRow label={TRIGGER_LABELS.callback} value={s.callbackCount ? `${s.callbackCount}× · ${formatMs(s.callbackTotal)}` : "none"} />
            <TipRow label={TRIGGER_LABELS.react} value={cycle.react ? `${formatMs(s.reactTotal)} · ${s.reactNodes} nodes` : "off"} />
        </>
    );
}

// --- Row bits ----------------------------------------------------------------

function Badges({ callbackCount, hasReact, templateCount }: { callbackCount: number; hasReact: boolean; templateCount?: number }) {
    return (
        <span className="cycle-badges">
            {templateCount != null && templateCount > 0 && <span className="cycle-badge tpl">{templateCount} ↯</span>}
            {callbackCount > 0 && <span className="cycle-badge cb">{callbackCount} cb</span>}
            {hasReact && <span className="cycle-badge react">react</span>}
        </span>
    );
}

function Chevron({ hasChildren, expanded, onToggle }: { hasChildren: boolean; expanded: boolean; onToggle: (e: React.MouseEvent) => void }) {
    return (
        <span
            className="cycle-chevron"
            onClick={hasChildren ? onToggle : undefined}
            style={{ visibility: hasChildren ? "visible" : "hidden" }}
        >
            {expanded ? "▾" : "▸"}
        </span>
    );
}

// --- Component ---------------------------------------------------------------

function CycleTree() {
    const dispatch = useThunkDispatch();
    const enabled = useAppSelector(s => s.profiling.enabled);
    const selectedCycleId = useAppSelector(s => s.profiling.selectedCycleId);
    // Select the whole slice so the forest recomputes only when profiling data changes.
    const profiling = useAppSelector(s => s.profiling);

    const groups = React.useMemo(
        () => groupSummaries(profiling)
                .toSorted((a, b) => a.startTime - b.startTime)
                .map(group => ({ group, tree: cycleTree(profiling, group) })),
        [profiling]
    );

    const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
    const [hover, setHover] = React.useState<Hover>(null);

    // Auto-expand each newest pass as it arrives, preserving prior expansions.
    const newestRootId = groups[0]?.group.rootCycleId;
    React.useEffect(() => {
        if (newestRootId) setExpanded(prev => (prev.has(newestRootId) ? prev : new Set(prev).add(newestRootId)));
    }, [newestRootId]);

    const toggle = React.useCallback((id: string) => {
        setExpanded(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    if (groups.length === 0) {
        return (
            <div className="cycle-list-empty">
                {enabled
                    ? "Interact with the canvas (e.g. change a parameter) to capture render passes."
                    : "Enable profiling to start capturing render passes."}
            </div>
        );
    }

    const renderRow = (opts: {
        cycleId: string;
        depth: number;
        hasChildren: boolean;
        isGroup: boolean;
        label: React.ReactNode;
        hover: HoverTarget;
    }) => {
        const { cycleId, depth, hasChildren, isGroup, label } = opts;
        const isExpanded = expanded.has(cycleId);
        return (
            <div
                className={`cycle-node-row${cycleId === selectedCycleId ? " selected" : ""}${isGroup ? " group" : ""}`}
                style={{ paddingLeft: 6 + depth * INDENT }}
                onClick={() => dispatch(selectCycle(cycleId))}
                onMouseMove={e => setHover({ ...opts.hover, x: e.clientX, y: e.clientY })}
                onMouseLeave={() => setHover(null)}
            >
                <Chevron hasChildren={hasChildren} expanded={isExpanded} onToggle={e => { e.stopPropagation(); toggle(cycleId); }} />
                {label}
            </div>
        );
    };

    const renderCycleNode = (node: CycleTreeNode, depth: number): React.ReactNode => {
        const { cycle } = node;
        const hasChildren = node.children.length > 0;
        return (
            <React.Fragment key={cycle.cycleId}>
                {renderRow({
                    cycleId: cycle.cycleId,
                    depth,
                    hasChildren,
                    isGroup: false,
                    hover: { kind: "cycle", cycle } as HoverTarget,
                    label: (
                        <div className="cycle-tree-label">
                            <div className="label-line primary">
                                <span className="cycle-file" style={{ direction: "rtl", textAlign: "left" }}>{cycle.fileName ?? "(inline)"}</span>
                                <span className="cycle-time">{formatMs(cycle.render?.totalTime ?? 0)}</span>
                            </div>
                            {(cycle.callbacks.length > 0 || cycle.react) && (
                                <div className="label-line secondary">
                                    <Badges callbackCount={cycle.callbacks.length} hasReact={!!cycle.react} />
                                </div>
                            )}
                        </div>
                    ),
                })}
                {hasChildren && expanded.has(cycle.cycleId) && node.children.map(child => renderCycleNode(child, depth + 1))}
            </React.Fragment>
        );
    };

    return (
        <div className="cycle-tree">
            {groups.map(({ group, tree }) => {
                const hasChildren = (tree?.children.length ?? 0) > 0;
                return (
                    <React.Fragment key={group.rootCycleId}>
                        {renderRow({
                            cycleId: group.rootCycleId,
                            depth: 0,
                            hasChildren,
                            isGroup: true,
                            hover: { kind: "group", group } as HoverTarget,
                            label: (
                                <div className="cycle-tree-label">
                                    <div className="label-line primary">
                                        <span className="cycle-id">{shortCycleId(group.rootCycleId)}</span>
                                        <span className="cycle-time">{formatMs(group.wallTime)}</span>
                                    </div>
                                    <div className="label-line secondary">
                                        <span className="cycle-file" style={{ direction: "rtl", textAlign: "left" }}>{group.fileName ?? "(inline)"}</span>
                                        <Badges callbackCount={group.callbackCount} hasReact={group.reactCount > 0} templateCount={group.templateCount} />
                                    </div>
                                </div>
                            ),
                        })}
                        {hasChildren && expanded.has(group.rootCycleId) && tree!.children.map(child => renderCycleNode(child, 1))}
                    </React.Fragment>
                );
            })}

            <ProfilingTooltip open={!!hover} x={hover?.x ?? 0} y={hover?.y ?? 0}>
                {hover && (hover.kind === "group"
                    ? <GroupTooltipContent group={hover.group} />
                    : <CycleTooltipContent cycle={hover.cycle} />)}
            </ProfilingTooltip>
        </div>
    );
}

export default React.memo(CycleTree);
